'use client';

/**
 * CollapsibleCard — per-card collapse/expand for the right-rail Insights
 * surface. Wraps each card at its single mounting point (`InsightsPanel`)
 * so no individual card file needs to change.
 *
 * Expanded: the card renders completely unchanged, in normal flow, with
 * a slim toggle strip stacked ABOVE it — deliberately not an absolute
 * overlay on top of the card's own header. An overlay was tried first
 * and collided with cards that have their own right-aligned header
 * controls (e.g. SynthesisCard's Pin/Refresh buttons) — no assumption
 * about a card's internal layout is safe to make, so the toggle lives
 * in its own separate block instead, guaranteeing zero overlap
 * regardless of what any given card renders.
 * Collapsed: only a compact icon + label bar renders — the card itself,
 * and its data fetch's consumer, isn't mounted at all. Exception:
 * `alwaysMounted` — opted into by cards (currently the synthesis-family
 * cards: mcp_health/health/quality/rfq_health) that publish a one-line
 * summary into `railCardPreviewStore` while they're alive, so a
 * collapsed synthesis card can still show "Plan Synthesis · amber ·
 * …" instead of a bare label. Those cards stay mounted-but-hidden
 * (`hidden`, not unmounted) while collapsed so that effect keeps
 * running; every other card keeps the fully-unmounted behavior above.
 *
 * @layer presentation
 * @arch_component_type wrapper
 * @arch_domain copilot
 */

import { ChevronDown, ChevronRight } from 'lucide-react';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { useRailCardCollapseStore } from '@/lib/stores/rail-card-collapse-store';
import { useRailCardPreviewStore } from '@/lib/stores/rail-card-preview-store';
import { cn } from '@/lib/utils/cn';
import { text } from '@/lib/design/tokens';

// Synthesis-shaped cards — the only ones today that publish a preview
// (see synthesis-card.tsx's effect). A frontend-only list because
// this is purely a rendering-cost tradeoff (stay mounted to keep a
// preview live vs. unmount to save the fetch), not something the
// backend matrix needs an opinion on.
const ALWAYS_MOUNTED_KEYS = new Set(['mcp_health', 'health', 'quality', 'rfq_health']);

export function CollapsibleCard({ cardKey, children }: { cardKey: string; children: React.ReactNode }) {
  const { label, Icon } = useCardIdentity();
  const collapsed = useRailCardCollapseStore((s) => !!s.collapsed[cardKey]);
  const toggle = useRailCardCollapseStore((s) => s.toggle);
  const preview = useRailCardPreviewStore((s) => s.preview[cardKey]);
  const alwaysMounted = ALWAYS_MOUNTED_KEYS.has(cardKey);

  if (collapsed) {
    return (
      <div>
        <button
          type="button"
          onClick={() => toggle(cardKey)}
          className="w-full flex items-center gap-2 px-4 py-3 rounded-xl border border-border bg-card hover:bg-muted/50 transition-colors"
        >
          {Icon && <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
          <span className="flex-1 min-w-0 text-left">
            <span className={cn(text.heading, 'block truncate')}>{label}</span>
            {preview && (
              <span className={cn(text.muted, 'block truncate text-muted-foreground')}>{preview}</span>
            )}
          </span>
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        </button>
        {alwaysMounted && <div className="hidden">{children}</div>}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-end px-1">
        <button
          type="button"
          onClick={() => toggle(cardKey)}
          aria-label={`Collapse ${label ?? 'card'}`}
          title={`Collapse ${label ?? 'card'}`}
          className="p-0.5 rounded text-muted-foreground/60 hover:text-foreground hover:bg-muted/50 transition-colors"
        >
          <ChevronDown className="w-3 h-3" />
        </button>
      </div>
      {children}
    </div>
  );
}
